import { useEffect, useMemo, useState } from "react";

import { usePageNameContext } from "../../contexts/PageTitleContext";
import useReports from "../../data/useReports";
import { IReport, parseReportDateFields } from "../../models/report/Report";
import Papa from "papaparse";
import { useParams } from "react-router-dom";
import LoadingWheel from "../shared/LoadingWheel";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import Card from "../shared/Card/Card";
import DynamicTable from "./DynamicTable";
import useNotifications from "../notifications/useNotifications";
import formatDateTime from "../../utils/formatDateTime";
import { DateTime } from "luxon";
import { InformationCircleIcon } from "@heroicons/react/24/outline";

const MAX_DISPLAY_ROWS = 500;

/** Blank option value: do not filter by date at all. */
const ALL_RECORDS = "";

export default function ViewReport() {
  const { setInfo } = usePageNameContext();
  const { reports, runReport } = useReports();
  const { reportId } = useParams();
  const { addNotification } = useNotifications();

  const [report, setReport] = useState<IReport | null>(null);
  const [reportData, setReportData] = useState<any>(null);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [dateField, setDateField] = useState<string>(ALL_RECORDS);

  const dateFields = useMemo(
    () => parseReportDateFields(report?.dateFields),
    [report?.dateFields]
  );

  // A report that declares date fields has been converted: the range is optional, and when one is
  // given it applies to the chosen column. A report that declares none is still on the legacy
  // shape handled below, where its SQL hardcodes the column and both dates are required. That
  // branch goes away once every report has been rewritten.
  const supportsFieldSelection = dateFields.length > 0;

  useEffect(() => {
    setInfo({
      name: `Reports`,
      desc: `View and download reports`,
    });
  }, [setInfo]);

  useEffect(() => {
    if (reportId && !reports.isLoading) {
      const foundReport = reports.data?.data.find((r) => r.id === reportId);
      setReport(foundReport || null);
    }
  }, [reportId, reports.data, reports.isLoading]);

  // Switching reports must not carry a field selection onto a report that never declared it: the
  // API rejects a field the report does not offer.
  useEffect(() => {
    setDateField(ALL_RECORDS);
    setStartDate(null);
    setEndDate(null);
    setReportData(null);
  }, [reportId]);

  const handleRunReport = (report: IReport) => {
    if (!report) return;

    const hasStart = !!startDate;
    const hasEnd = !!endDate;

    if (supportsFieldSelection) {
      // Mirrors ReportDateFilterRules.Validate on the API so the common mistakes are caught
      // without a round trip. The API still checks: this is convenience, not the guard.
      if (hasStart !== hasEnd) {
        addNotification({
          variant: "error",
          primaryText: "Enter both a start and an end date, or neither",
        });
        return;
      }

      if (hasStart && !dateField) {
        addNotification({
          variant: "error",
          primaryText: "Choose which date the range applies to",
        });
        return;
      }

      if (!hasStart && dateField) {
        addNotification({
          variant: "error",
          primaryText: "Enter a date range, or choose All records to run over everything",
        });
        return;
      }
    } else if (report.allowDates) {
      // Legacy: unchanged from before date fields existed.
      if (!hasStart || !hasEnd) {
        addNotification({
          variant: "error",
          primaryText: "Please select a start and end date",
        });
        return;
      }
    }

    runReport.mutate(
      {
        id: report.id,
        startDate,
        endDate,
        dateField: supportsFieldSelection ? dateField || null : null,
      },
      {
        onSuccess: (data) => {
          setReportData(data.data);
        },
        onError: (error: any) => {
          // The API returns 400 with a readable message for a rejected date combination and for a
          // run that exceeded the command timeout; useApiHelper lifts it onto the Error.
          addNotification({
            variant: "error",
            primaryText: error?.message || "Unable to run report",
          });
        },
      }
    );
  };

  const handleCsvDownload = (reportData: any) => {
    if (!reportData) return;

    const csv = Papa.unparse(reportData);

    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");

    a.href = url;
    a.download = `${report?.title}-${formatDateTime(new Date().toISOString(), DateTime.DATE_SHORT)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (reports.isLoading) return <LoadingWheel />

  if (report === null) return <div>Report not found</div>

  const totalRows = Array.isArray(reportData) ? reportData.length : 0;
  const isTruncated = totalRows > MAX_DISPLAY_ROWS;
  const displayData = isTruncated ? reportData.slice(0, MAX_DISPLAY_ROWS) : reportData;

  const showDateInputs = supportsFieldSelection || report.allowDates;
  const datesDisabled = supportsFieldSelection && !dateField;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">

        <div className="flex items-center gap-4">
          {showDateInputs && (
            <div className="flex gap-x-4 pr-6">
              {supportsFieldSelection && (
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Filter On
                  </label>
                  <select
                    aria-label="Date field"
                    value={dateField}
                    onChange={(e) => setDateField(e.target.value)}
                    className="w-full py-1 px-1 mt-1 text-sm text-gray-900 border border-border"
                  >
                    <option value={ALL_RECORDS}>All records</option>
                    {dateFields.map((field) => (
                      <option key={field.key} value={field.key}>
                        {field.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="text-sm font-medium text-gray-500">
                  Start Date
                </label>
                <input
                  aria-label="Date from"
                  type="date"
                  value={startDate || ""}
                  disabled={datesDisabled}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full py-1 px-1 mt-1 text-sm text-gray-900 border border-border disabled:bg-gray-100 disabled:text-gray-400"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">
                  End Date
                </label>
                <input
                  aria-label="Date to"
                  type="date"
                  value={endDate || ""}
                  disabled={datesDisabled}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full py-1 px-1 mt-1 text-sm text-gray-900 border border-border disabled:bg-gray-100 disabled:text-gray-400"
                />
              </div>
            </div>
          )}
          <div className="mt-7">
            <ButtonInput label="Run Report" isLoading={runReport.isLoading} onClick={() => handleRunReport(report)} classes="" />
          </div>
        </div>

        <div className="flex items-center gap-4 mt-7">
          <ButtonInput label="Download" disabled={!reportData} onClick={() => handleCsvDownload(reportData)} classes="" />
        </div>

      </div>

      {isTruncated && (
        <div className="flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-700">
          <InformationCircleIcon className="h-5 w-5 flex-shrink-0 text-blue-500" />
          <span>
            Showing the first {MAX_DISPLAY_ROWS.toLocaleString()} of{" "}
            {totalRows.toLocaleString()} rows. Download the report to get all rows.
          </span>
        </div>
      )}

      <div>
        <Card>
          <DynamicTable data={displayData} />
        </Card>
      </div>
    </div>
  );
}
